<template>
	<div
		data-testid="help-assistant-action-card"
		role="group"
		:aria-label="`Suggested action: ${action.summary}`"
		class="help-action-card"
	>
		<p class="help-action-card__title">
			Suggested action
		</p>
		<p class="help-action-card__summary">
			{{ action.summary }}
		</p>

		<div
			v-if="isResolved"
			data-testid="help-assistant-action-card-status"
			role="status"
			:class="['help-action-card__outcome', { 'help-action-card__outcome--error': action.status === 'error' }]"
		>
			<LucideIcon
				v-if="action.status === 'applied'"
				name="Check"
				:size="16"
				class="help-action-card__outcome-icon"
			/>
			{{ outcomeText }}
		</div>
		<div
			v-else
			class="help-action-card__buttons"
		>
			<help-button
				data-testid="help-assistant-action-card-confirm"
				variant="primary"
				:disabled="action.status === 'confirming'"
				class="help-action-card__button"
				@click="$emit('confirm', { actionId: action.actionId })"
			>
				Confirm
			</help-button>
			<help-button
				data-testid="help-assistant-action-card-dismiss"
				variant="outline"
				:disabled="action.status === 'confirming'"
				class="help-action-card__button"
				@click="$emit('dismiss', { actionId: action.actionId })"
			>
				Dismiss
			</help-button>
		</div>
	</div>
</template>

<script>
import { LucideIcon } from '@syrenis/cassie-material-design'
import HelpButton from './help-button.vue'

export default {
	name: 'help-action-card',
	components: {
		HelpButton,
		LucideIcon
	},
	props: {
		action: {
			type: Object,
			required: true,
			// { actionId, toolName, summary, status: 'pending'|'confirming'|'applied'|'dismissed'|'error' }
			validator: value => typeof value.actionId === 'string' && typeof value.summary === 'string'
		}
	},
	computed: {
		isResolved () {
			return ['applied', 'dismissed', 'error'].includes(this.action.status)
		},
		outcomeText () {
			if (this.action.status === 'applied') return 'Applied'
			if (this.action.status === 'dismissed') return 'Dismissed'
			if (this.action.status === 'error') return 'Something went wrong. Please try again.'
			return ''
		}
	}
}
</script>

<style lang="scss" scoped>
.help-action-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid var(--border, #E0E2E5);
  border-radius: var(--radius, 8px);
  background-color: var(--card, #FFFFFF);

  &__title {
    margin: 0;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--muted-foreground, #666E7A);
    text-transform: uppercase;
  }

  // Control labels sit at 13px across the assistant; Vuetify's `small` renders them at 12px.
  &__button {
    font-size: 0.8125rem;
  }

  &__summary {
    margin: 0;
    font-size: 0.875rem;
    color: var(--foreground, #4A5568);
  }

  &__buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  &__button {
    min-height: 24px;
    min-width: 24px;
  }

  &__outcome {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 0.75rem;
    color: var(--muted-foreground, #666E7A);

    &--error {
      color: var(--destructive, #E53E3E);
    }
  }

  &__outcome-icon {
    color: var(--success, #389854);
  }
}
</style>
